/**
 * `/rolls` — the DTF film-roll registry. Register the physical films you print on so gang sheets
 * nest at the real usable width (e.g. 34″ on a 35.43″ film) instead of the engine default, and cap
 * the sheet length you cut. One roll is the default a new batch nests onto without an explicit pick.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, Pencil, Plus, RefreshCw, Scroll, Star, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiClient } from '@/lib/api/client';
import { useApiClient } from '@/lib/api/hooks';
import type { components } from '@/lib/api/types.gen';

type RollSummary = components['schemas']['RollSummary'];

function useRolls() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['rolls'],
    queryFn: async () => {
      const { data, error } = await client.GET('/rolls');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load rolls.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data.items as RollSummary[];
    },
  });
}

export function RollsPage() {
  const query = useRolls();
  const rolls = query.data ?? [];

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Film rolls</h1>
          <p className="text-sm text-muted-foreground">
            The DTF films you print on. Usable width is the printable width nesting packs into (e.g.
            34″ on a 35.43″ film); max length caps the sheet you cut. The default roll is used when
            a batch is built without a pick.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            <RefreshCw className={query.isFetching ? 'size-4 animate-spin' : 'size-4'} />
            Refresh
          </Button>
          <RollDialog mode="create" />
        </div>
      </header>

      {query.isError && (
        <Alert variant="destructive">
          <AlertTitle>Couldn't load rolls</AlertTitle>
          <AlertDescription>
            {query.error instanceof Error ? query.error.message : 'Network error.'}
          </AlertDescription>
        </Alert>
      )}

      {query.isPending && <Skeleton className="h-40 w-full" />}

      {query.isSuccess && (rolls.length === 0 ? <EmptyState /> : <RollTable rolls={rolls} />)}
    </div>
  );
}

function RollTable({ rolls }: { rolls: RollSummary[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full min-w-[520px] text-sm">
        <thead className="bg-muted/40">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Roll</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
              Usable width
            </th>
            <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
              Max length
            </th>
            <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground sr-only">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {rolls.map((roll) => (
            <tr key={roll.rollId} className="border-t [&:nth-child(even)]:bg-muted/20">
              <td className="px-3 py-2 align-top">
                <div className="flex items-center gap-2">
                  <Scroll className="size-4 shrink-0 text-muted-foreground" />
                  <span className="font-medium">{roll.name}</span>
                  {roll.isDefault && (
                    <Badge variant="outline" className="border-amber-300 text-amber-700">
                      <Star className="size-3" /> Default
                    </Badge>
                  )}
                </div>
              </td>
              <td className="px-3 py-2 text-right align-top tabular-nums">{roll.usableWidthIn}″</td>
              <td className="px-3 py-2 text-right align-top tabular-nums">{roll.maxLengthIn}″</td>
              <td className="px-3 py-2 text-right align-top">
                <RollRowActions roll={roll} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RollRowActions({ roll }: { roll: RollSummary }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<null | 'edit' | 'delete'>(null);

  const setDefault = useMutation({
    mutationFn: async () => {
      const res = await client.POST('/rolls/{rollId}/default', {
        params: { path: { rollId: roll.rollId } },
      });
      if (res.error || !res.data?.ok) throw new Error(res.error?.error?.detail ?? 'Failed.');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rolls'] }),
  });

  return (
    <div className="flex items-center justify-end gap-1">
      {!roll.isDefault && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDefault.mutate()}
          disabled={setDefault.isPending}
          title="Make default"
        >
          {setDefault.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Star className="size-4" />
          )}
          Default
        </Button>
      )}
      <Button variant="ghost" size="icon" onClick={() => setDialog('edit')} aria-label="Edit roll">
        <Pencil className="size-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="text-destructive"
        onClick={() => setDialog('delete')}
        aria-label="Delete roll"
      >
        <Trash2 className="size-4" />
      </Button>
      <RollDialog
        mode="edit"
        roll={roll}
        open={dialog === 'edit'}
        onClose={() => setDialog(null)}
      />
      <DeleteRollDialog roll={roll} open={dialog === 'delete'} onClose={() => setDialog(null)} />
    </div>
  );
}

async function saveRoll(
  client: ApiClient,
  body: { name: string; usableWidthIn: number; maxLengthIn: number; isDefault?: boolean },
  rollId?: string,
): Promise<void> {
  const res = rollId
    ? await client.PATCH('/rolls/{rollId}', { params: { path: { rollId } }, body })
    : await client.POST('/rolls', { body });
  if (res.error || !res.data?.ok) {
    throw new Error(res.error?.error?.detail ?? 'Could not save the roll.');
  }
}

function RollDialog({
  mode,
  roll,
  open: controlledOpen,
  onClose,
}: {
  mode: 'create' | 'edit';
  roll?: RollSummary;
  open?: boolean;
  onClose?: () => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [selfOpen, setSelfOpen] = useState(false);
  const open = mode === 'create' ? selfOpen : (controlledOpen ?? false);
  const close = () => (mode === 'create' ? setSelfOpen(false) : onClose?.());

  const [name, setName] = useState(roll?.name ?? '');
  const [width, setWidth] = useState(roll ? String(roll.usableWidthIn) : '');
  const [length, setLength] = useState(roll ? String(roll.maxLengthIn) : '');
  const [makeDefault, setMakeDefault] = useState(roll?.isDefault ?? false);

  useEffect(() => {
    if (open) {
      setName(roll?.name ?? '');
      setWidth(roll ? String(roll.usableWidthIn) : '');
      setLength(roll ? String(roll.maxLengthIn) : '');
      setMakeDefault(roll?.isDefault ?? false);
    }
  }, [open, roll]);

  const w = Number(width);
  const l = Number(length);
  const valid = name.trim().length > 0 && w > 0 && w <= 120 && l > 0 && l <= 240;

  const mutation = useMutation({
    mutationFn: async () => {
      await saveRoll(
        client,
        { name: name.trim(), usableWidthIn: w, maxLengthIn: l, isDefault: makeDefault },
        roll?.rollId,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rolls'] });
      close();
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (mode === 'create') setSelfOpen(o);
        if (!o) {
          mutation.reset();
          onClose?.();
        }
      }}
    >
      {mode === 'create' && (
        <Button size="sm" onClick={() => setSelfOpen(true)}>
          <Plus className="size-4" />
          New roll
        </Button>
      )}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Register a film roll' : 'Edit roll'}</DialogTitle>
          <DialogDescription>
            Usable width is the printable width nesting packs into. Max length is the longest sheet
            you'll cut from this roll (the per-build length picker can't exceed it).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="roll-name">Name</Label>
            <Input
              id="roll-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='Color Prime Quick Glide 35.43" × 109yd'
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="roll-width">Usable width (in)</Label>
              <Input
                id="roll-width"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="1"
                max="120"
                value={width}
                onChange={(e) => setWidth(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="roll-length">Max length (in)</Label>
              <Input
                id="roll-length"
                type="number"
                inputMode="decimal"
                step="1"
                min="1"
                max="240"
                value={length}
                onChange={(e) => setLength(e.target.value)}
              />
            </div>
          </div>
          <label className="flex flex-wrap items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4 accent-primary"
              checked={makeDefault}
              // The default is promote-only (there's no "un-default" — a roll stops being default when
              // another is promoted). So on the roll that IS the default, lock the box checked rather
              // than presenting an un-check that the backend would silently ignore.
              disabled={mode === 'edit' && roll?.isDefault === true}
              onChange={(e) => setMakeDefault(e.target.checked)}
            />
            Use this roll by default for new gang sheets
            {mode === 'edit' && roll?.isDefault === true && (
              <span className="text-xs text-muted-foreground">
                — already the default; promote another roll to change it
              </span>
            )}
          </label>
          {mutation.isError && (
            <Alert variant="destructive">
              <AlertDescription>
                {mutation.error instanceof Error ? mutation.error.message : 'Save failed.'}
              </AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter>
          <Button onClick={() => mutation.mutate()} disabled={!valid || mutation.isPending}>
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            {mode === 'create' ? 'Register roll' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteRollDialog({
  roll,
  open,
  onClose,
}: {
  roll: RollSummary;
  open: boolean;
  onClose: () => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => {
      const res = await client.DELETE('/rolls/{rollId}', {
        params: { path: { rollId: roll.rollId } },
      });
      if (res.error || !res.data?.ok) throw new Error(res.error?.error?.detail ?? 'Delete failed.');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rolls'] });
      onClose();
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          mutation.reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete “{roll.name}”?</DialogTitle>
          <DialogDescription>
            Removes it from the roll list. Batches already built with it are unaffected.
          </DialogDescription>
        </DialogHeader>
        {mutation.isError && (
          <Alert variant="destructive">
            <AlertDescription>
              {mutation.error instanceof Error ? mutation.error.message : 'Delete failed.'}
            </AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Check className="size-4" />
            )}
            Delete roll
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed py-16 text-center">
      <Scroll className="size-8 text-muted-foreground" />
      <p className="text-sm font-medium">No rolls registered</p>
      <p className="max-w-sm text-sm text-muted-foreground">
        Add the DTF film you print on so gang sheets nest at its real usable width instead of the
        22″ default.
      </p>
    </div>
  );
}
